import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Result } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHead } from '../components/PageHead';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

type EventInfo = {
  id: string;
  title: string;
  date: string | null;
  status: 'draft' | 'live' | 'ended';
  embed_code: string | null;
  rehearsal_vmix_id: string | null;
};

export function RehearsalScene() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [info, setInfo] = useState<EventInfo | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!eventId) return;
    void (async () => {
      try {
        setInfo(await api<EventInfo>(`/api/event/${eventId}/info`));
      } finally {
        setLoaded(true);
      }
    })();
  }, [eventId]);

  if (!loaded) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <PageHead title="Rehearsal" />
        <p>Loading…</p>
      </div>
    );
  }

  if (!info?.rehearsal_vmix_id) {
    return (
      <div style={{ padding: 32 }}>
        <PageHead title="Rehearsal" />
        <Result
          status="info"
          title="Rehearsal channel not set up"
          subTitle="The team hasn't configured a rehearsal vMix channel for this event yet. Ask them to add one in the event's edit page."
          extra={
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => eventId && navigate(`/waiting/${eventId}`)}
            >
              Back to waiting room
            </Button>
          }
        />
      </div>
    );
  }

  const name = session?.email ?? '';
  const src = `https://advanced.vmixcall.com/call.htm?Key=${encodeURIComponent(
    info.rehearsal_vmix_id,
  )}&Name=${encodeURIComponent(`[REHEARSAL] ${name}`)}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#fff' }}>
      <PageHead title="Rehearsal" />
      <div
        style={{
          padding: '8px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: '#fff7e6',
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => eventId && navigate(`/waiting/${eventId}`)}
        >
          Back to waiting room
        </Button>
        <Alert
          type="warning"
          showIcon={false}
          message="Rehearsal channel — not on the broadcast feed. Check your mic, camera, lighting, framing."
          banner
          style={{ flex: 1, background: 'transparent', border: 0, padding: 0 }}
        />
      </div>
      <iframe
        title="AOTV vMix Rehearsal"
        src={src}
        allow="camera; microphone; fullscreen; autoplay; display-capture"
        allowFullScreen
        style={{ border: 0, width: '100%', flex: 1, background: '#fff' }}
      />
    </div>
  );
}
